import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionHead } from "../components/Reveal";
import { TECHNOLOGIES } from "../data/content";

const CATS = ["All", ...TECHNOLOGIES.map((c) => c.category)];

export default function Technologies() {
  const [cat, setCat] = useState("All");
  const items = TECHNOLOGIES.flatMap((c) => c.items.map((name) => ({ name, category: c.category })));
  const visible = cat === "All" ? items : items.filter((t) => t.category === cat);

  return (
    <main className="pt-28">
      <section data-testid="technologies-section" className="section-pad !pt-8">
        <div className="container-x">
          <SectionHead badge="Tech Stack" title="Technologies We Master"
            sub="A battle-tested stack covering frontend, backend, cloud and AI — chosen for your problem, never for fashion." />
          <div className="grid sm:grid-cols-3 gap-5 mb-12">
            {[
              "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80",
              "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=800&q=80",
            ].map((src, i) => (
              <motion.img key={src} src={src} alt="VAELIX technology work"
                data-testid={`tech-banner-img-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="rounded-2xl border border-border/60 shadow-lg w-full h-40 sm:h-48 object-cover hover:scale-[1.03] transition-transform duration-300" />
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-2.5 mb-12" role="tablist" aria-label="Technology categories">
            {CATS.map((c) => (
              <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)}
                data-testid={`tech-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 border ${
                  cat === c
                    ? "bg-gradient-to-r from-indigo-500 to-blue-500 text-white border-transparent shadow-[0_6px_20px_rgba(99,102,241,0.4)]"
                    : "border-border bg-card/60 text-muted-foreground hover:text-foreground hover:border-primary/40"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <motion.div layout className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            <AnimatePresence mode="popLayout">
              {visible.map((t) => (
                <motion.div key={t.name} layout data-testid={`tech-card-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }} whileHover={{ y: -6 }}
                  className="glass-card rounded-2xl p-5 text-center">
                  <span className="mx-auto w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/15 to-cyan-400/15 border border-primary/20 flex items-center justify-center font-display font-extrabold text-lg text-gradient">
                    {t.name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase()}
                  </span>
                  <h3 className="mt-3 text-sm font-semibold">{t.name}</h3>
                  <p className="mt-0.5 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{t.category}</p>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
